import { Easing, interpolate } from 'remotion';
import { Effect, EffectType } from '@/player-config/src/types/RemotionPlayerConfigTypes/Animations/Effects';

/**
 * Returns a CSS style object for the given frame and effects.
 * @param frame The current frame number
 * @param effects Array of Effect
 */
export function getAnimationStylesForFrame(
  frame: number,
  effects: Effect[] = [],
  fps: number
): { style: React.CSSProperties; effectBounds: { fromFrame: number; toFrame: number } } {
  if (effects.length === 0) {
    return { style: {}, effectBounds: { fromFrame: 0, toFrame: 0 } };
  }

  const style: React.CSSProperties = {};
  const effectBounds = {
    fromFrame: 0,
    toFrame: 0
  };

  // Helper function to get easing function based on animation type
  const getEasingFunction = (animationType: 'easeInAndOut' | 'linear' | 'bounce' = 'easeInAndOut') => {
    switch (animationType) {
      case 'linear':
        return Easing.linear;
      case 'bounce':
        return Easing.bounce;
      case 'easeInAndOut':
      default:
        return Easing.inOut(Easing.ease);
    }
  };

  // Handle Effects (e.g., Zoom)
  effects.forEach(effect => {
    const effectStartFrame = effect.start * fps;
    const effectEndFrame = effectStartFrame + effect.duration * fps;

    // Use animationDuration if present, otherwise fall back to effect.duration
    const animationInDuration = effect.animationInDuration || 0;
    const animationInEndFrame = effectStartFrame + animationInDuration * fps;

    const animationOutDuration = effect.animationOutDuration || 0;
    const animationOutStartFrame = effectEndFrame - animationOutDuration * fps;

    const effectDurationInFrames = effect.duration * fps;

    if (frame >= effectStartFrame && frame <= effectEndFrame && effect.enabled !== false) {
      effectBounds.fromFrame = effectStartFrame;
      effectBounds.toFrame = effectEndFrame;
      if (effect.type === EffectType.Zoom) {
        let progress: number;
        if (animationInDuration && frame <= animationInEndFrame) {
          progress = interpolate(frame - effectStartFrame, [0, animationInDuration * fps], [0, 1], {
            easing: getEasingFunction(effect.animationType)
          });
          const scale = 1 + (effect.params.baseScale - 1) * progress;
          style.transform = `${style.transform || ''} scale(${scale})`;
        } else if (frame <= animationOutStartFrame) {
          progress = interpolate(
            frame - animationInEndFrame,
            [0, effectDurationInFrames - animationInDuration - animationOutDuration],
            [0, 1],
            {
              easing: getEasingFunction(effect.animationType)
            }
          );
          const scale = effect.params.baseScale + (effect.params.toScale - effect.params.baseScale) * progress;
          style.transform = `${style.transform || ''} scale(${scale})`;
        } else {
          progress = interpolate(frame - animationOutStartFrame, [0, animationOutDuration * fps], [0, 1], {
            easing: getEasingFunction(effect.animationType)
          });
          const scale = effect.params.toScale + (1 - effect.params.toScale) * progress;
          style.transform = `${style.transform || ''} scale(${scale})`;
        }
        // Optionally handle centerX/centerY as transform-origin
        if (effect.params.centerX !== undefined && effect.params.centerY !== undefined) {
          style.transformOrigin = `${effect.params.centerX * 100}% ${effect.params.centerY * 100}%`;
        }
      }
    }
  });

  return { style, effectBounds };
}
